// Fixkosten verwalten: Miete, Versicherungen, Leasing, Abos.
//
// Die Vorlagen nehmen die häufigsten Fälle vorweg — samt der Feinheiten, die
// man sonst falsch macht: Versicherungen und Kfz-Steuer tragen keine
// Umsatzsteuer, Kfz-Versicherung gehört zu den Fahrzeugkosten, nicht zu den
// allgemeinen Versicherungen. Wer die Vorlage nimmt, liegt damit richtig.
import { useCallback, useEffect, useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { dateiHochladen, type Anhang } from '../../firma/lib/dateien';
import { euro, datum } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Auswahl, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Intervall = 'monatlich' | 'vierteljaehrlich' | 'halbjaehrlich' | 'jaehrlich';

type Posten = {
  id: string; bezeichnung: string; kategorie: string; kategorieName: string;
  brutto: number; netto: number; vorsteuer: number; steuersatz: number;
  intervall: Intervall; intervallName: string; tag: number;
  beginn: string; ende: string | null; lieferant: string; vertragsnummer: string; zahlart: string; notiz: string;
  dateiIds: string[]; aktiv: boolean; nurLiquiditaet?: boolean;
  proMonat: number; proJahr: number; naechsterTermin: string | null; anzahlGebucht: number; summeGebucht: number;
};

type Liste = {
  posten: Posten[];
  summe: { proMonat: number; proJahr: number; anzahl: number; betriebsausgabenProMonat: number; nurLiquiditaetProMonat: number };
  soebenGebucht: number;
};

type Vorlage = {
  name: string;
  kategorie: string;
  steuersatz: number;
  intervall: Intervall;
  tag?: number;
  /** Fester Phasenbeginn, z. B. Steuervorauszahlungen am 10.03./06./09./12. */
  beginn?: (jahr: number) => string;
  /** Geht vom Konto, ist aber keine Betriebsausgabe. */
  nurLiquiditaet?: boolean;
  tipp?: string;
};

// Gruppiert nach Lebensbereichen des Betriebs. Die Steuersätze sind die
// üblichen — die Fallstricke stehen im Tipp, der beim Anlegen erscheint.
const VORLAGEN: Array<{ gruppe: string; vorlagen: Vorlage[] }> = [
  { gruppe: 'Räume', vorlagen: [
    { name: 'Miete', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich',
      tipp: 'Gewerbemiete ist oft umsatzsteuerfrei — steht im Vertrag keine Umsatzsteuer, auf 0 % stellen.' },
    { name: 'Nebenkosten', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Strom', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich',
      tipp: 'Den monatlichen Abschlag eintragen. Nach- oder Rückzahlung der Jahresabrechnung als einzelne Ausgabe erfassen.' },
    { name: 'Gas / Heizung', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich',
      tipp: 'Den monatlichen Abschlag eintragen. Die Jahresabrechnung als einzelne Ausgabe erfassen.' },
    { name: 'Wasser', kategorie: 'raumkosten', steuersatz: 7, intervall: 'vierteljaehrlich',
      tipp: 'Leitungswasser hat 7 % Umsatzsteuer, Abwasser meist keine. Steht beides auf einer Rechnung, zwei Posten anlegen.' },
    { name: 'Müllentsorgung', kategorie: 'raumkosten', steuersatz: 19, intervall: 'vierteljaehrlich',
      tipp: 'Kommunale Müllgebühren sind umsatzsteuerfrei (0 %), private Entsorger berechnen 19 %.' },
    { name: 'Reinigung', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Lager / Stellfläche', kategorie: 'raumkosten', steuersatz: 19, intervall: 'monatlich' },
  ] },
  { gruppe: 'Fahrzeug', vorlagen: [
    { name: 'Kfz-Versicherung', kategorie: 'fahrzeug', steuersatz: 0, intervall: 'jaehrlich',
      tipp: 'Versicherungen tragen keine Umsatzsteuer — deshalb 0 %. Gehört zu den Fahrzeugkosten, nicht zu den allgemeinen Versicherungen.' },
    { name: 'Kfz-Steuer', kategorie: 'fahrzeug', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Leasing', kategorie: 'fahrzeug', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Garage / Stellplatz', kategorie: 'fahrzeug', steuersatz: 19, intervall: 'monatlich' },
  ] },
  { gruppe: 'Versicherungen & Beiträge', vorlagen: [
    { name: 'Betriebshaftpflicht', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Inhaltsversicherung', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Betriebsunterbrechung', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Rechtsschutz', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Cyberversicherung', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'IHK-Beitrag', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Handwerkskammer', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Berufsgenossenschaft', kategorie: 'versicherung', steuersatz: 0, intervall: 'jaehrlich' },
    { name: 'Rundfunkbeitrag', kategorie: 'versicherung', steuersatz: 0, intervall: 'vierteljaehrlich',
      tipp: 'Für Betriebsstätten und betrieblich genutzte Fahrzeuge. Ohne Umsatzsteuer.' },
  ] },
  { gruppe: 'Telefon & IT', vorlagen: [
    { name: 'Telefon & Internet', kategorie: 'kommunikation', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Mobilfunk', kategorie: 'kommunikation', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Website & Hosting', kategorie: 'sonstiges', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Software-Abo', kategorie: 'sonstiges', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Cloud-Speicher', kategorie: 'sonstiges', steuersatz: 19, intervall: 'monatlich' },
  ] },
  { gruppe: 'Kasse & Bank', vorlagen: [
    { name: 'Kartenterminal', kategorie: 'sonstiges', steuersatz: 19, intervall: 'monatlich',
      tipp: 'Die Miete des Geräts. Transaktionsgebühren schwanken und gehören nicht hierher.' },
    { name: 'Kassen-TSE', kategorie: 'sonstiges', steuersatz: 19, intervall: 'jaehrlich',
      tipp: 'Die Sicherheitseinrichtung ist für elektronische Kassen Pflicht (§ 146a AO).' },
    { name: 'Kontoführung', kategorie: 'gebuehren', steuersatz: 0, intervall: 'monatlich',
      tipp: 'Bankgebühren sind umsatzsteuerfrei.' },
    { name: 'Darlehenszinsen', kategorie: 'gebuehren', steuersatz: 0, intervall: 'monatlich',
      tipp: 'Nur der Zinsanteil ist Betriebsausgabe. Die Tilgung als eigenen Posten „Darlehenstilgung" anlegen.' },
  ] },
  { gruppe: 'Personal', vorlagen: [
    { name: 'Minijob', kategorie: 'personal', steuersatz: 0, intervall: 'monatlich',
      tipp: 'Lohn plus Pauschalabgaben an die Minijob-Zentrale — beides zusammen eintragen.' },
    { name: 'Gehalt', kategorie: 'personal', steuersatz: 0, intervall: 'monatlich',
      tipp: 'Arbeitgeberbrutto eintragen, also mit den Arbeitgeberanteilen zur Sozialversicherung.' },
  ] },
  { gruppe: 'Beratung & Sonstiges', vorlagen: [
    { name: 'Steuerberater', kategorie: 'beratung', steuersatz: 19, intervall: 'monatlich' },
    { name: 'Wartungsvertrag', kategorie: 'sonstiges', steuersatz: 19, intervall: 'jaehrlich' },
    { name: 'Alarmanlage', kategorie: 'sonstiges', steuersatz: 19, intervall: 'monatlich' },
    { name: 'GEMA', kategorie: 'sonstiges', steuersatz: 7, intervall: 'jaehrlich',
      tipp: 'Für Musik im Laden oder Wartebereich. Lizenzen tragen 7 % Umsatzsteuer.' },
    { name: 'Branchenbuch & Portale', kategorie: 'werbung', steuersatz: 19, intervall: 'jaehrlich' },
  ] },
  { gruppe: 'Steuern, Kredite & Entnahmen', vorlagen: [
    { name: 'Einkommensteuer-Vorauszahlung', kategorie: 'sonstiges', steuersatz: 0, intervall: 'vierteljaehrlich',
      tag: 10, beginn: j => `${j}-03-10`, nurLiquiditaet: true,
      tipp: 'Fällig am 10. März, Juni, September und Dezember — Betrag steht im Vorauszahlungsbescheid, Kirchensteuer und Soli gleich mit. Keine Betriebsausgabe: erscheint nur in Vorschau und Kontostand.' },
    { name: 'Gewerbesteuer-Vorauszahlung', kategorie: 'sonstiges', steuersatz: 0, intervall: 'vierteljaehrlich',
      tag: 15, beginn: j => `${j}-02-15`, nurLiquiditaet: true,
      tipp: 'Fällig am 15. Februar, Mai, August und November. Seit 2008 keine Betriebsausgabe mehr — erscheint nur in Vorschau und Kontostand.' },
    { name: 'Darlehenstilgung', kategorie: 'sonstiges', steuersatz: 0, intervall: 'monatlich', nurLiquiditaet: true,
      tipp: 'Nur die Tilgung — sie verringert die Schuld und ist keine Ausgabe. Die Zinsen gehören als „Darlehenszinsen" unter Kasse & Bank.' },
    { name: 'Privatentnahme', kategorie: 'sonstiges', steuersatz: 0, intervall: 'monatlich', nurLiquiditaet: true,
      tipp: 'Was du dir monatlich als Unternehmerlohn überweist. Nur bei Einzelunternehmen und Personengesellschaften — bei einer GmbH ist es Geschäftsführergehalt unter Personal.' },
  ] },
];

const KATEGORIEN: Array<[string, string]> = [
  ['raumkosten', 'Miete und Raumkosten'], ['fahrzeug', 'Fahrzeugkosten'],
  ['versicherung', 'Versicherungen und Beiträge'], ['kommunikation', 'Telefon und Internet'],
  ['beratung', 'Rechts- und Steuerberatung'], ['personal', 'Personalkosten'],
  ['werbung', 'Werbekosten'], ['gebuehren', 'Bankgebühren und Zinsen'], ['sonstiges', 'Sonstige Betriebsausgaben'],
];

const OHNE_VORSTEUER = new Set(['versicherung', 'personal', 'gebuehren']);

export default function Fixkosten() {
  const [daten, setDaten] = useState<Liste | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeite, setBearbeite] = useState<Posten | 'neu' | null>(null);
  const [vorlage, setVorlage] = useState<Vorlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await api<Liste>('/fixkosten');
      setDaten(d);
      if (d.soebenGebucht) setMeldung(`${d.soebenGebucht} fällige Zahlung(en) wurden soeben gebucht.`);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Fixkosten konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  async function pausieren(p: Posten) {
    try {
      await api(`/fixkosten/${p.id}/pausieren`, { methode: 'POST', daten: { aktiv: !p.aktiv } });
      setMeldung(p.aktiv ? `„${p.bezeichnung}" pausiert — es wird nichts mehr gebucht.` : `„${p.bezeichnung}" läuft wieder ab heute.`);
      await laden();
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Ging nicht.'); }
  }

  async function loeschen(p: Posten) {
    if (!confirm(`„${p.bezeichnung}" entfernen?\n\nBereits gebuchte Zahlungen (${p.anzahlGebucht}) bleiben in der Buchhaltung — es kommen nur keine neuen mehr dazu.`)) return;
    try {
      await api(`/fixkosten/${p.id}`, { methode: 'DELETE' });
      setMeldung(`„${p.bezeichnung}" entfernt.`);
      await laden();
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Ging nicht.'); }
  }

  if (fehler && !daten) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (!daten) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Karte klasse="p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Pro Monat</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(daten.summe.proMonat)}</p>
          {daten.summe.nurLiquiditaetProMonat > 0 && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {euro(daten.summe.betriebsausgabenProMonat)} Ausgaben · {euro(daten.summe.nurLiquiditaetProMonat)} nur Konto
            </p>
          )}
        </Karte>
        <Karte klasse="p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Pro Jahr</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(daten.summe.proJahr)}</p>
        </Karte>
        <Karte klasse="p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Laufende Posten</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{daten.summe.anzahl}</p>
        </Karte>
      </div>

      <Erfolgsmeldung text={meldung} />
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      {bearbeite && (
        <PostenFormular
          posten={bearbeite === 'neu' ? null : bearbeite}
          vorlage={bearbeite === 'neu' ? vorlage : null}
          beiAbbruch={() => { setBearbeite(null); setVorlage(null); }}
          beiErfolg={async text => { setBearbeite(null); setVorlage(null); setMeldung(text); await laden(); }} />
      )}

      {!bearbeite && (
        <Karte>
          <KartenKopf titel="Neuer Posten" hinweis="Vorlage wählen oder frei anlegen. Gebucht wird automatisch am Fälligkeitstag." />
          <div className="space-y-4 p-5">
            {VORLAGEN.map(g => (
              <div key={g.gruppe}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{g.gruppe}</p>
                <div className="flex flex-wrap gap-2">
                  {g.vorlagen.map(v => (
                    <button key={v.name} type="button" onClick={() => { setVorlage(v); setBearbeite('neu'); setMeldung(null); }}
                            className={`rounded-lg border px-3 py-1.5 text-sm transition ${v.nurLiquiditaet
                              ? 'border-dashed border-slate-300 text-slate-600 hover:border-amber-400 hover:bg-amber-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-amber-700 dark:hover:bg-amber-950/30'
                              : 'border-slate-300 text-slate-700 hover:border-marke-400 hover:bg-marke-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-marke-700 dark:hover:bg-marke-950/30'}`}>
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button type="button" onClick={() => { setVorlage(null); setBearbeite('neu'); setMeldung(null); }}
                    className="rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:border-slate-400 dark:border-slate-700 dark:text-slate-400">
              + Eigener Posten
            </button>
          </div>
        </Karte>
      )}

      <Karte>
        <KartenKopf titel="Laufende Fixkosten" />
        {daten.posten.length === 0 ? (
          <LeerZustand titel="Noch keine Fixkosten"
                       text="Miete, Versicherungen, Leasing einmal anlegen — danach bucht die App sie jeden Monat selbst." />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {daten.posten.map(p => (
              <li key={p.id} className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 ${p.aktiv ? '' : 'opacity-60'}`}>
                <div className="min-w-[12rem] flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                    {p.bezeichnung}
                    {!p.aktiv && <Abzeichen art="gelb">pausiert</Abzeichen>}
                    {p.nurLiquiditaet && <Abzeichen art="gelb">nur Konto</Abzeichen>}
                    {p.dateiIds.length > 0 && <Abzeichen>Vertrag hinterlegt</Abzeichen>}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {p.kategorieName} · {euro(p.brutto)} {p.intervallName}
                    {p.nurLiquiditaet ? '' : p.steuersatz > 0 ? ` · ${p.steuersatz} % USt` : ' · ohne USt'}
                    {p.lieferant && ` · an ${p.lieferant}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="tabular-nums font-semibold text-slate-900 dark:text-slate-100">{euro(p.proMonat)}<span className="text-xs font-normal text-slate-500"> / Mon.</span></p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {p.naechsterTermin ? `nächste ${datum(p.naechsterTermin)}` : (p.aktiv ? 'beendet' : '—')}
                    {p.anzahlGebucht > 0 && ` · ${p.anzahlGebucht}× gebucht`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Knopf variante="still" className="!text-xs" onClick={() => { setBearbeite(p); setMeldung(null); }}>Bearbeiten</Knopf>
                  <Knopf variante="still" className="!text-xs" onClick={() => pausieren(p)}>{p.aktiv ? 'Pausieren' : 'Fortsetzen'}</Knopf>
                  <Knopf variante="still" className="!text-xs !text-red-600 dark:!text-red-400" onClick={() => loeschen(p)}>Entfernen</Knopf>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PostenFormular({ posten, vorlage, beiAbbruch, beiErfolg }: {
  posten: Posten | null; vorlage: Vorlage | null;
  beiAbbruch: () => void; beiErfolg: (text: string) => Promise<void>;
}) {
  const heute = new Date().toISOString().slice(0, 10);
  const [nurLiquiditaet, setNurLiquiditaet] = useState(posten?.nurLiquiditaet ?? vorlage?.nurLiquiditaet ?? false);
  const [f, setF] = useState({
    bezeichnung: posten?.bezeichnung ?? vorlage?.name ?? '',
    kategorie: posten?.kategorie ?? vorlage?.kategorie ?? 'sonstiges',
    brutto: posten ? String(posten.brutto).replace('.', ',') : '',
    steuersatz: String(posten?.steuersatz ?? vorlage?.steuersatz ?? 19),
    intervall: (posten?.intervall ?? vorlage?.intervall ?? 'monatlich') as Intervall,
    tag: String(posten?.tag ?? vorlage?.tag ?? 1),
    beginn: posten?.beginn ?? vorlage?.beginn?.(new Date().getFullYear()) ?? heute,
    ende: posten?.ende ?? '',
    lieferant: posten?.lieferant ?? '',
    vertragsnummer: posten?.vertragsnummer ?? '',
    zahlart: posten?.zahlart ?? 'lastschrift',
    notiz: posten?.notiz ?? '',
  });
  const [nachbuchen, setNachbuchen] = useState(false);
  const [vertrag, setVertrag] = useState<Anhang | null>(null);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  const setze = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(x => ({ ...x, [k]: e.target.value }));
  const ohneVorsteuer = OHNE_VORSTEUER.has(f.kategorie);
  // Nachbuchen ergibt nur bei echten Ausgaben Sinn — Posten ohne Buchung
  // haben nichts nachzubuchen.
  const inVergangenheit = !posten && !nurLiquiditaet && f.beginn < heute;

  async function vertragHochladen(liste: FileList | null) {
    if (!liste?.[0]) return;
    setLaedtHoch(true);
    try { setVertrag(await dateiHochladen(liste[0])); }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Upload fehlgeschlagen.'); }
    finally { setLaedtHoch(false); }
  }

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true); setFehler(null);
    const daten = {
      ...f,
      nurLiquiditaet,
      brutto: Number(f.brutto.replace(/\./g, '').replace(',', '.')),
      steuersatz: ohneVorsteuer || nurLiquiditaet ? 0 : Number(f.steuersatz),
      tag: Number(f.tag),
      ende: f.ende || null,
      ...(vertrag ? { dateiIds: [...(posten?.dateiIds ?? []), vertrag.id] } : {}),
      ...(nachbuchen ? { nachbuchen: true } : {}),
    };
    try {
      if (posten) {
        await api(`/fixkosten/${posten.id}`, { methode: 'PUT', daten });
        await beiErfolg(`„${f.bezeichnung}" gespeichert. Die Änderung gilt für alle künftigen Buchungen.`);
      } else {
        const r = await api<{ soebenGebucht: number }>('/fixkosten', { methode: 'POST', daten });
        await beiErfolg(r.soebenGebucht
          ? `„${f.bezeichnung}" angelegt — ${r.soebenGebucht} fällige Zahlung(en) direkt gebucht.`
          : `„${f.bezeichnung}" angelegt. Gebucht wird automatisch am Fälligkeitstag.`);
      }
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
      <Karte klasse="border-marke-300 dark:border-marke-800">
        <KartenKopf titel={posten ? `${posten.bezeichnung} bearbeiten` : 'Neuer Fixkostenposten'}
                    hinweis={posten ? 'Bereits gebuchte Zahlungen bleiben unverändert.' : undefined} />
        <form onSubmit={speichern} className="space-y-4 p-5">
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          {vorlage?.tipp && !posten && <Hinweis art={vorlage.nurLiquiditaet ? 'warnung' : 'info'}>{vorlage.tipp}</Hinweis>}

          <label className="flex cursor-pointer items-start gap-3">
            <input type="checkbox" checked={nurLiquiditaet} onChange={e => setNurLiquiditaet(e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Keine Betriebsausgabe — geht nur vom Konto
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                Für Steuervorauszahlungen, Darlehenstilgung und Privatentnahmen. Erscheint in Vorschau und Kontostand,
                aber nie in EÜR oder Umsatzsteuer.
              </span>
            </span>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <Feld label="Bezeichnung" value={f.bezeichnung} onChange={setze('bezeichnung')} required autoFocus />
            {!nurLiquiditaet && (
              <Auswahl label="Kategorie" value={f.kategorie} onChange={setze('kategorie')}>
                {KATEGORIEN.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </Auswahl>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <Feld label="Betrag brutto" value={f.brutto} onChange={setze('brutto')} inputMode="decimal" placeholder="z. B. 1.190,00" required />
            <Auswahl label="Intervall" value={f.intervall} onChange={setze('intervall')}>
              <option value="monatlich">monatlich</option>
              <option value="vierteljaehrlich">vierteljährlich</option>
              <option value="halbjaehrlich">halbjährlich</option>
              <option value="jaehrlich">jährlich</option>
            </Auswahl>
            <Feld label="Zahltag (1–31)" value={f.tag} onChange={setze('tag')} inputMode="numeric"
                  hinweis="31 = immer Monatsletzter" />
            {nurLiquiditaet ? (
              <Feld label="Steuersatz" value="—" disabled hinweis="Keine Ausgabe, keine Steuer" />
            ) : ohneVorsteuer ? (
              <Feld label="Steuersatz" value="0 %" disabled hinweis="Diese Kategorie trägt keine USt" />
            ) : (
              <Auswahl label="Steuersatz" value={f.steuersatz} onChange={setze('steuersatz')}>
                <option value="19">19 %</option>
                <option value="7">7 %</option>
                <option value="0">0 % (umsatzsteuerfrei)</option>
              </Auswahl>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <Feld label="Beginn" type="date" value={f.beginn} onChange={setze('beginn')} required />
            <Feld label="Ende (optional)" type="date" value={f.ende} onChange={setze('ende')} min={f.beginn} />
            <Feld label="Zahlungsempfänger" value={f.lieferant} onChange={setze('lieferant')} placeholder="z. B. Vermieter" />
            <Auswahl label="Zahlart" value={f.zahlart} onChange={setze('zahlart')}>
              <option value="lastschrift">Lastschrift</option>
              <option value="ueberweisung">Dauerauftrag / Überweisung</option>
              <option value="karte">Karte</option>
              <option value="bar">Bar</option>
              <option value="privat">Privat bezahlt</option>
            </Auswahl>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Feld label="Vertrags- oder Policennummer" value={f.vertragsnummer} onChange={setze('vertragsnummer')} />
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Vertrag als Dauerbeleg</span>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                  {laedtHoch ? 'Lädt …' : vertrag ? 'Anderen wählen' : 'Datei wählen'}
                  <input type="file" accept="application/pdf,image/*" className="hidden" onChange={e => vertragHochladen(e.target.files)} />
                </label>
                <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {vertrag ? vertrag.name : posten?.dateiIds.length ? `${posten.dateiIds.length} hinterlegt` : 'hängt dann an jeder Buchung'}
                </span>
              </div>
            </label>
          </div>

          {inVergangenheit && (
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
              <input type="checkbox" checked={nachbuchen} onChange={e => setNachbuchen(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span className="text-sm text-amber-900 dark:text-amber-100">
                Vergangene Zahlungen seit {datum(f.beginn)} nachbuchen
                <span className="mt-0.5 block text-xs text-amber-800 dark:text-amber-200">
                  Nur ankreuzen, wenn diese Zahlungen noch <strong>nicht</strong> von Hand als Ausgabe erfasst sind — sonst stehen sie doppelt drin.
                  Ohne Haken bucht die App ab heute.
                </span>
              </span>
            </label>
          )}

          <div className="flex gap-2">
            <Knopf type="submit" variante="primaer" laedt={sendet}>{posten ? 'Speichern' : 'Anlegen'}</Knopf>
            <Knopf type="button" onClick={beiAbbruch}>Abbrechen</Knopf>
          </div>
        </form>
      </Karte>
    </Blickfang>
  );
}
